import { getPhoneCallOutcomeLabel } from "../../models/phoneCallOutcomes";
import { FiCornerRightUp, FiMail, FiPhone } from "react-icons/fi";
import "./ContactsWidget.css";
import { useMemo, useState } from "react";
import AddPhoneCall from "../modals/AddPhoneCall";

const ALL_ACCOUNTS = "all";

function formatInteractionTime(value) {
    if (!value) {
        return "Unknown time";
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function ContactsWidget({ contacts = [], calls = [], emails = [], deals = [], loading, error, onContactSelect, onRefresh }){
    const [currentContact, selectCurrentContact] = useState(null);
    const [addConversationOpen, setAddConversationOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedAccount, setSelectedAccount] = useState(ALL_ACCOUNTS);
    const [historySort, setHistorySort] = useState("newest");

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const accounts = useMemo(() => (
        [...new Set(contacts.map((contact) => String(contact?.account || "").trim()).filter((account) => account && account !== "-"))]
            .sort((left, right) => left.localeCompare(right))
    ), [contacts]);
    const filteredContacts = contacts.filter((contact) => {
        const matchesAccount = selectedAccount === ALL_ACCOUNTS || String(contact?.account || "") === selectedAccount;
        const matchesSearch = !normalizedSearch || [contact?.name, contact?.mobile, contact?.email]
            .some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
        return matchesAccount && matchesSearch;
    });
    const currentConversations = useMemo(() => {
        if (!currentContact) {
            return [];
        }
        const callHistory = calls
            .filter((call) => String(call.contact_id) === String(currentContact.id))
            .map((call) => ({
                id: `call-${call.id}`,
                type: "call",
                occurredAt: call.call_at,
                record: call,
            }));
        const emailHistory = emails.map((email) => ({
            id: `email-${email.graph_message_id || email.id}`,
            type: "email",
            occurredAt: email.sent_at,
            record: email,
        }));

        return [...callHistory, ...emailHistory]
            .sort((left, right) => {
                const leftTime = new Date(left?.occurredAt || 0).getTime();
                const rightTime = new Date(right?.occurredAt || 0).getTime();
                return historySort === "oldest" ? leftTime - rightTime : rightTime - leftTime;
            });
    }, [calls, currentContact, emails, historySort]);

    function selectContact(contact) {
        selectCurrentContact(contact);
        onContactSelect?.(contact);
    }

    return(
        <div className="contacts-widget">
            <div className="contacts-list">
                <div className="contacts-list-controls">
                    <select
                        value={selectedAccount}
                        onChange={(event) => {
                            setSelectedAccount(event.target.value);
                            selectContact(null);
                        }}
                        aria-label="Filter contacts by company"
                    >
                        <option value={ALL_ACCOUNTS}>All companies</option>
                        {accounts.map((account) => <option key={account} value={account}>{account}</option>)}
                    </select>
                    <input
                        type="search"
                        placeholder="Search contacts"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        className="search-bar"
                        aria-label="Search contacts"
                    />
                </div>
                <div className="contacts-list-items">
                    {filteredContacts.map((contact) => (
                        <button
                            key={String(contact.id)}
                            type="button"
                            className={`contact-item ${String(currentContact?.id) === String(contact.id) ? "is-active" : ""}`}
                            onClick={() => selectContact(contact)}
                            aria-pressed={String(currentContact?.id) === String(contact.id)}
                        >
                            <strong>{contact.name || "Unnamed contact"} <FiCornerRightUp /></strong>
                            {contact.email ? <span>{contact.email}</span> : null}
                            {contact.mobile ? <span>{contact.mobile}</span> : null}
                        </button>
                    ))}
                    {filteredContacts.length === 0 ? <p className="contacts-empty">No contacts found.</p> : null}
                </div>
            </div>

            <div className="contacts-view">
                {currentContact ? (
                    <div className="contact-view-content">
                        <div className="contact-detail">
                            <div className="contact-heading">
                                <strong>{currentContact.name || "Unnamed contact"}</strong>
                                <span>{currentContact.account || "No account"}</span>
                            </div>
                            <div className="contact-info">
                                <span><FiMail /> {currentContact.email || "No email"}</span>
                                <span><FiPhone /> {currentContact.mobile || "No phone"}</span>
                                <span>Next step: {currentContact.next_touch || "None scheduled"}</span>
                            </div>
                            <button type="button" className="add-conversation-button" onClick={() => setAddConversationOpen(true)}>
                                Add Conversation
                            </button>
                        </div>

                        <div className="user-conversations">
                            <div className="conversation-history-header">
                                <div>
                                    <strong>Conversation history</strong>
                                    <span>{currentConversations.length} {currentConversations.length === 1 ? "interaction" : "interactions"}</span>
                                </div>
                                <select value={historySort} onChange={(event) => setHistorySort(event.target.value)} aria-label="Sort conversation history">
                                    <option value="newest">Newest first</option>
                                    <option value="oldest">Oldest first</option>
                                </select>
                            </div>
                            {loading ? <p className="contacts-empty">Loading conversation history...</p> : null}
                            {!loading && error ? <p className="contacts-empty">{error}</p> : null}
                            {!loading && !error && currentConversations.length === 0 ? (
                                <p className="contacts-empty">No conversations logged for this contact.</p>
                            ) : null}
                            {currentConversations.map((conversation) => {
                                const record = conversation.record;
                                const isEmail = conversation.type === "email";
                                const emailContact = String(record.direction || "").toLowerCase() === "inbound"
                                    ? record.from_email
                                    : record.to_email;
                                return (
                                <article className="deal-conversation" key={conversation.id}>
                                    <div className="conversation-summary">
                                        <strong>{formatInteractionTime(conversation.occurredAt)}</strong>
                                        <span>{String(record.direction || "outbound").toUpperCase()}</span>
                                        <span>{isEmail ? "Email" : `${record.call_type || "Call"} · ${Math.max(Number(record.duration_seconds) || 0, 0)}s`}</span>
                                        <span>{isEmail ? record.subject || "No subject" : getPhoneCallOutcomeLabel(record.outcome)}</span>
                                        <span>{isEmail ? emailContact || "Unknown contact" : record.user_name || "Unknown user"}</span>
                                    </div>
                                    <div className="conversation-notes">
                                        <strong>{isEmail ? "Message" : "Notes"}</strong>
                                        <p>{isEmail ? record.body_text || "No message preview" : record.notes || "No notes"}</p>
                                    </div>
                                </article>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <p className="no-contact-selected">Select a contact to view conversation history.</p>
                )}
            </div>

            <AddPhoneCall
                isOpen={addConversationOpen}
                onClose={() => setAddConversationOpen(false)}
                deals={deals}
                contacts={contacts}
                contactId={currentContact?.id}
                title="Add Conversation"
                onCreated={() => {
                    setAddConversationOpen(false);
                    onRefresh?.();
                }}
            />
        </div>
    );
}

export default ContactsWidget;